/**
 * Utility functions for parsing and formatting time slot duration strings
 * Supports formats like: "15min", "30min", "1h", "2h", "90min", etc.
 */

/**
 * Parses a duration string to minutes
 * @param duration - Duration string (e.g., "15min", "30min", "1h", "2h")
 * @returns Number of minutes, or null if invalid
 */
export const parseDurationToMinutes = (duration: string | number): number | null => {
  // If it's already a number, return it
  if (typeof duration === 'number') {
    return duration;
  }

  if (!duration || typeof duration !== 'string') {
    return null;
  }

  const trimmed = duration.trim().toLowerCase();

  // Handle hour format (e.g., "1h", "2h", "1.5h")
  if (trimmed.endsWith('h')) {
    const hours = parseFloat(trimmed.slice(0, -1));
    if (!isNaN(hours) && hours > 0) {
      return Math.round(hours * 60);
    }
  }

  // Handle minute format (e.g., "15min", "30min", "90min")
  if (trimmed.endsWith('min')) {
    const minutes = parseFloat(trimmed.slice(0, -3));
    if (!isNaN(minutes) && minutes > 0) {
      return Math.round(minutes);
    }
  }

  // Handle just numbers (assume minutes)
  const numeric = parseFloat(trimmed);
  if (!isNaN(numeric) && numeric > 0) {
    return Math.round(numeric);
  }

  return null;
};

/**
 * Formats minutes to a duration string
 * @param minutes - Number of minutes
 * @returns Formatted string (e.g., "15min", "30min", "1h")
 */
export const formatMinutesToDuration = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined || minutes <= 0) {
    return '';
  }

  // If it's a multiple of 60, use hours
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1h' : `${hours}h`;
  }

  // Otherwise use minutes
  return `${minutes}min`;
};

/**
 * Validates a duration string
 * @param duration - Duration string to validate
 * @returns Validation result with isValid flag and optional error message
 */
export interface DurationValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateDurationString = (duration: string): DurationValidationResult => {
  if (!duration || !duration.trim()) {
    return {
      isValid: false,
      error: 'Duration cannot be empty',
    };
  }

  const parsed = parseDurationToMinutes(duration);
  if (parsed === null) {
    return {
      isValid: false,
      error: 'Invalid duration format. Use formats like "15min", "30min", "1h"',
    };
  }

  if (parsed <= 0) {
    return {
      isValid: false,
      error: 'Duration must be greater than 0',
    };
  }

  return { isValid: true };
};

/**
 * Normalizes a duration string to a standard format
 * @param duration - Duration string to normalize
 * @returns Normalized string (e.g., "15min", "1h")
 */
export const normalizeDurationString = (duration: string | number): string => {
  const minutes = parseDurationToMinutes(duration);
  if (minutes === null) {
    return '';
  }
  return formatMinutesToDuration(minutes);
};
